// ═══════════════════════════════════════════════════════════════
// EMAIL TEMPLATES PANEL — Settings tab for customizing the email
// templates offered in QuickLogModal (Renewal Ask, LYBNT, SYBNT,
// 2nd Gift Ask, Thank You). Admin-only.
//
// Storage: app_settings.email_templates (JSON blob keyed by
// template key). Defaults live in constants/emailTemplates.js
// and show through whenever an override is blank.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import {
  Mail,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Check,
  Sparkles,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { Card } from "../ui/Card";
import {
  EMAIL_TEMPLATE_DEFINITIONS,
  DEFAULT_EMAIL_TEMPLATES,
} from "../../constants/emailTemplates";
import { emailTemplatesService } from "../../services/emailTemplatesService";
import { replaceTokens, buildEmailContext } from "../../utils/receiptTokens";

const TOKEN_REFERENCE = [
  { token: "{donorFirstName}", desc: 'Donor\'s first name (or "Friend")' },
  { token: "{donorFullName}", desc: "Donor's full or household name" },
  { token: "{chapterName}", desc: "Organization name" },
  { token: "{signatureName}", desc: "Your name from Organization settings" },
  { token: "{signatureTitle}", desc: "Your title from Organization settings" },
  { token: "{lastGiftAmount}", desc: "Most recent gift amount" },
  { token: "{lastGiftDate}", desc: "Most recent gift date (long format)" },
  { token: "{totalGiving}", desc: "Lifetime giving total" },
];

// Preview uses a synthetic donor so admins can see how the
// template reads before saving.
const PREVIEW_CONTEXT_DONOR = {
  firstName: "Sarah",
  lastName: "Bennett",
  credentialedFullName: "Sarah Bennett, CFRE",
  donorType: "individual",
};
const PREVIEW_GIFT_SUMMARY = {
  lastGiftDate: "2025-10-14",
  lastGiftAmount: 250,
  totalGiving: 1850,
};

const TokenCheatSheet = ({ palette }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        marginTop: 8,
        borderRadius: 8,
        border: `1px solid ${palette.cardBorder}`,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          width: "100%",
          padding: "10px 14px",
          border: "none",
          background: `${palette.accent1}08`,
          color: palette.text2,
          fontSize: FONT_SIZES.xs,
          fontWeight: 700,
          fontFamily: FONT_FAMILY,
          cursor: "pointer",
        }}
      >
        {open ? <ChevronDown size={ICON_SIZES.sm} /> : <ChevronRight size={ICON_SIZES.sm} />}
        Available tokens
      </button>
      {open && (
        <div style={{ padding: 12, background: palette.cardBg }}>
          <table style={{ width: "100%", fontSize: 12, fontFamily: FONT_FAMILY }}>
            <tbody>
              {TOKEN_REFERENCE.map((t) => (
                <tr key={t.token}>
                  <td
                    style={{
                      padding: "3px 8px 3px 0",
                      color: palette.accent1,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      verticalAlign: "top",
                    }}
                  >
                    {t.token}
                  </td>
                  <td style={{ padding: "3px 0", color: palette.text2 }}>
                    {t.desc}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TemplateEditor = ({ definition, palette, chapterSettings, template, onSave, onReset, saving }) => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [dirty, setDirty] = useState(false);

  // Sync when external template changes (e.g. after save/reset)
  React.useEffect(() => {
    setSubject(template.subject);
    setBody(template.body);
    setDirty(false);
  }, [template.subject, template.body]);

  const preview = useMemo(() => {
    const ctx = buildEmailContext(
      PREVIEW_CONTEXT_DONOR,
      chapterSettings,
      PREVIEW_GIFT_SUMMARY
    );
    return {
      subject: replaceTokens(subject, ctx),
      body: replaceTokens(body, ctx),
    };
  }, [subject, body, chapterSettings]);

  const handleSave = async () => {
    await onSave(definition.key, { subject, body });
    setDirty(false);
  };

  const handleReset = async () => {
    const def = DEFAULT_EMAIL_TEMPLATES[definition.key];
    setSubject(def.subject);
    setBody(def.body);
    await onReset(definition.key);
    setDirty(false);
  };

  const labelStyle = {
    display: "block",
    fontSize: FONT_SIZES.xs,
    fontWeight: 700,
    color: palette.text2,
    marginBottom: 6,
  };

  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    outline: "none",
    background: palette.inputBg,
    color: palette.inputColor,
    border: `1px solid ${palette.inputBorder}`,
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        borderRadius: 10,
        border: `1px solid ${palette.cardBorder}`,
        background: palette.cardBg,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "12px 14px",
          border: "none",
          background: "transparent",
          color: palette.text1,
          fontSize: FONT_SIZES.sm,
          fontWeight: 700,
          fontFamily: FONT_FAMILY,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {open ? <ChevronDown size={ICON_SIZES.sm} /> : <ChevronRight size={ICON_SIZES.sm} />}
        <Sparkles size={ICON_SIZES.sm} color={palette.accent1} />
        <span>{definition.label}</span>
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            fontWeight: 400,
            color: palette.text3,
          }}
        >
          — {definition.description}
        </span>
        {template.isCustomized && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 10,
              fontWeight: 700,
              color: palette.accent1,
              background: `${palette.accent1}15`,
              padding: "3px 8px",
              borderRadius: 99,
              letterSpacing: 0.3,
            }}
          >
            CUSTOMIZED
          </span>
        )}
      </button>

      {open && (
        <div style={{ padding: "4px 14px 14px 14px" }}>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setDirty(true);
              }}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Body</label>
            <textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setDirty(true);
              }}
              rows={12}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
            />
          </div>

          <TokenCheatSheet palette={palette} />

          <div
            style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 8,
              background: `${palette.accent1}08`,
              border: `1px dashed ${palette.cardBorder}`,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: palette.text2,
                marginBottom: 6,
                letterSpacing: 0.3,
              }}
            >
              PREVIEW — Sarah Bennett, $250 gift on Oct 14, 2025
            </div>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 700,
                color: palette.text1,
                marginBottom: 8,
              }}
            >
              {preview.subject}
            </div>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                color: palette.text2,
                whiteSpace: "pre-wrap",
                lineHeight: 1.5,
              }}
            >
              {preview.body}
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            {template.isCustomized && (
              <button
                onClick={handleReset}
                disabled={saving}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${palette.cardBorder}`,
                  background: "transparent",
                  color: palette.text2,
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  cursor: saving ? "default" : "pointer",
                }}
              >
                <RotateCcw size={ICON_SIZES.sm} /> Reset to default
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: dirty ? palette.accent1 : palette.cardBorder,
                color: "#fff",
                fontSize: FONT_SIZES.sm,
                fontWeight: 700,
                fontFamily: FONT_FAMILY,
                cursor: dirty && !saving ? "pointer" : "default",
                opacity: dirty ? 1 : 0.5,
              }}
            >
              <Check size={ICON_SIZES.sm} /> {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const EmailTemplatesPanel = ({ palette }) => {
  const {
    emailTemplates,
    setEmailTemplates,
    chapterSettings,
    showMessage,
  } = useApp();
  const [saving, setSaving] = useState(false);

  const handleSave = async (templateKey, patch) => {
    setSaving(true);
    try {
      const updated = await emailTemplatesService.update(templateKey, patch);
      setEmailTemplates(updated);
      showMessage("Template saved");
    } catch (err) {
      showMessage("Could not save template", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateKey) => {
    setSaving(true);
    try {
      const updated = await emailTemplatesService.reset(templateKey);
      setEmailTemplates(updated);
      showMessage("Template reset to default");
    } catch (err) {
      showMessage("Could not reset template", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <Mail size={ICON_SIZES.md} color={palette.accent1} />
        <div
          style={{
            fontSize: FONT_SIZES.base,
            fontWeight: 700,
            color: palette.text1,
            fontFamily: FONT_FAMILY,
          }}
        >
          Email Templates
        </div>
      </div>
      <p
        style={{
          color: palette.text2,
          fontSize: FONT_SIZES.sm,
          margin: "0 0 16px 0",
          lineHeight: 1.5,
        }}
      >
        These templates appear in the Log Contact modal when you pick the
        email type. Customize the wording to match your chapter's voice —
        defaults ship with sensible copy so you can start sending today.
      </p>

      {EMAIL_TEMPLATE_DEFINITIONS.map((def) => (
        <TemplateEditor
          key={def.key}
          definition={def}
          palette={palette}
          chapterSettings={chapterSettings}
          template={emailTemplates[def.key]}
          onSave={handleSave}
          onReset={handleReset}
          saving={saving}
        />
      ))}
    </Card>
  );
};
